// SpiceBoardRegistration.jsx
import React from "react";

export default function SpiceBoardRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Spice Board Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Spice Board Registration for Exporters
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists exporters and manufacturers with Spice
            Board Registration for compliance with mandatory export requirements
            under the Spices Board Act, 1986.
          </p>
          <p className="text-gray-700">
            The Certificate of Registration as Exporter of Spices (CRES) is
            obtained through the official Spice Board portal with complete
            documentation, fee payment, and regional office verification.
          </p>
        </div>

        {/* Right: Image (SPICE BOARD REGISTRATION - Indian spices, export documentation, professional compliance) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/2347691/pexels-photo-2347691.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Spice exporter obtaining Spice Board Registration"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides end-to-end assistance for Spice Board
              Registration (CRES), mandatory for all spice exporters under the
              Spices Board Act.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Online registration on Spice Board portal with IEC verification
                  and application reference number generation.
                </li>
                <li>
                  Complete Form-1 preparation with manufacturer/merchant exporter
                  details and supporting document compilation.
                </li>
                <li>
                  Fee payment management and coordination with regional Spice
                  Board offices for document scrutiny and certificate issuance.
                </li>
                <li>
                  Renewal support and compliance advisory for continuous export
                  eligibility across 52 listed spices.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for Spice Board Registration
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks helps organize mandatory documents for CRES
              application through the Spice Board online portal.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Valid Import Export Code (IEC) certificate.</li>
                <li>PAN card of the business entity and authorised signatory.</li>
                <li>
                  Business incorporation documents (Partnership Deed,
                  Incorporation Certificate, or Proprietorship Declaration).
                </li>
                <li>
                  Self-attested copy of GST registration certificate and FSSAI
                  license (for food safety compliance).
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in spice export compliance with proven success
              in securing Spice Board Registration for manufacturers and merchants.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Export Compliance Knowledge
                </h3>
                <p className="text-gray-700 text-sm">
                  Comprehensive understanding of Spices Board Act requirements,
                  CRES application process, and 52 spice product classifications.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Registration Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional handling of online portal applications, document
                  uploads, and regional office coordination for timely CRES issuance.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Trusted Professional Service
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end support from eligibility verification through
                  certificate download and ongoing renewal compliance management.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Start Your Spice Export Journey
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to obtain your Spice Board
                Registration (CRES) and legally export Indian spices worldwide
                with regulatory compliance and quality assurance.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
